import { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import {
  TrendingDown,
  TrendingUp,
  BarChart3,
  LineChart,
  Info,
  Lightbulb,
  Target,
  Sun,
  Cloud,
  Snowflake,
  Flower2,
  ArrowLeftRight,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { Spinner } from "../components/ui/Spinner";
import { useRequireAuth } from "../hooks/useAuth";
import { useSeasonalityStore } from "../stores/seasonalityStore";
import { ArriviPresenzeSection } from "../components/seasonality/ArriviPresenzeSection";
import { ComparisonChart } from "../components/seasonality/ComparisonChart";
import { Autocomplete } from "../components/ui/Autocomplete";
import {
  useAvailableYears,
  useIstatPresenze,
  useIstatIndicators,
  useAllIstatIndicators,
  useIstatCapacity,
} from "../hooks/useIstatData";
import { cn } from "../lib/utils";
import type { SeasonalityIndicator, IstatPresenzeMensili } from "../types/database";

const monthNames = [
  "Gen", "Feb", "Mar", "Apr", "Mag", "Giu",
  "Lug", "Ago", "Set", "Ott", "Nov", "Dic"
];

const RESIDENZE = [
  { value: "mondo", label: "Totale mondo" },
  { value: "italia", label: "Italia" },
  { value: "esteri", label: "Paesi esteri" },
];

const TIPOLOGIE = [
  { value: "totale", label: "Totale esercizi" },
  { value: "alberghiero", label: "Esercizi alberghieri" },
  { value: "extra-alberghiero", label: "Esercizi extra-alberghieri" },
];

const seasonIcons = {
  winter: Snowflake,
  spring: Flower2,
  summer: Sun,
  autumn: Cloud,
};

function getSeasonFromMonth(month: number): keyof typeof seasonIcons {
  if (month >= 3 && month <= 5) return "spring";
  if (month >= 6 && month <= 8) return "summer";
  if (month >= 9 && month <= 11) return "autumn";
  return "winter";
}

function formatNum(n: number | null | undefined): string {
  if (n === null || n === undefined) return "-";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(0) + "K";
  return n.toFixed(1);
}

export function Seasonality() {
  const { isLoading: authLoading } = useRequireAuth();

  // Store state
  const {
    activeTab,
    setActiveTab,
    chartView,
    setChartView,
    istatTerritorio,
    setIstatTerritorio,
    istatAnno,
    setIstatAnno,
    istatResidenza,
    setIstatResidenza,
    istatTipologia,
    setIstatTipologia,
    comparisonTerritory,
    setComparisonTerritory,
  } = useSeasonalityStore();

  // Local state
  const [showComparison, setShowComparison] = useState(false);

  // Available years per tab (from DB)
  const { data: availableYears } = useAvailableYears();
  const currentTabYears = useMemo(() => {
    if (!availableYears) return [];
    return activeTab === "arrivi" ? availableYears.arrivi : availableYears.presenze;
  }, [availableYears, activeTab]);

  const yearOptions = useMemo(() =>
    currentTabYears.map(y => ({ value: String(y), label: String(y) })),
    [currentTabYears]
  );

  // Auto-select first available year, reset on tab change if invalid
  const effectiveAnno = (currentTabYears.includes(istatAnno) ? istatAnno : currentTabYears[0]) || 0;
  useEffect(() => {
    if (currentTabYears.length > 0 && !currentTabYears.includes(istatAnno)) {
      setIstatAnno(currentTabYears[0]);
    }
  }, [currentTabYears, istatAnno, setIstatAnno]);

  // ISTAT data queries — only run when we have a valid year
  const { data: presenze = [], isLoading: presenzeLoading } = useIstatPresenze(
    istatTerritorio || undefined, effectiveAnno || undefined, istatResidenza || undefined, istatTipologia || undefined
  );
  const { data: indicators = [] } = useIstatIndicators(
    istatTerritorio || undefined, effectiveAnno || undefined, istatResidenza || undefined, istatTipologia || undefined
  );
  const { data: allIndicators = [] } = useAllIstatIndicators(
    effectiveAnno || undefined, istatResidenza || undefined, istatTipologia || undefined
  );
  const { data: capacityData = [] } = useIstatCapacity(
    istatTerritorio || undefined, effectiveAnno || undefined
  );
  const { data: comparisonPresenze = [] } = useIstatPresenze(
    comparisonTerritory || undefined, effectiveAnno || undefined, istatResidenza || undefined, istatTipologia || undefined
  );

  // Derived data
  const currentIndicator = indicators.length > 0 ? indicators[0] : null;
  const currentCapacity = capacityData.length > 0 ? capacityData[0] : null;

  // Territory list from all indicators (exclude national aggregate)
  const EXCLUDED_TERRITORIES = new Set(["Italia"]);
  const territories = useMemo((): string[] => {
    const set = new Set(allIndicators.map((i: SeasonalityIndicator) => i.territorio));
    EXCLUDED_TERRITORIES.forEach(t => set.delete(t));
    return Array.from(set).sort() as string[];
  }, [allIndicators]);

  // Autocomplete options
  const territoryOptions = useMemo(() =>
    [{ value: "Italia", label: "Italia" }, ...territories.map(t => ({ value: t, label: t }))],
    [territories]
  );
  const comparisonOptions = useMemo(() =>
    ["Italia", ...territories].filter(t => t !== istatTerritorio).map(t => ({ value: t, label: t })),
    [territories, istatTerritorio]
  );


  // Monthly chart data
  const chartData = useMemo(() => {
    if (presenze.length === 0) return null;
    const monthly = Array.from({ length: 12 }, (_, i) => {
      const found = presenze.find((p: IstatPresenzeMensili) => p.mese === i + 1);
      return found?.presenze || 0;
    });
    const max = Math.max(...monthly, 1);
    const mean = monthly.reduce((s, v) => s + v, 0) / 12;
    const indexed = monthly.map((v, i) => ({ v, i }));
    const sorted = [...indexed].sort((a, b) => b.v - a.v);
    const top3 = new Set(sorted.slice(0, 3).map(x => x.i));
    const bottom3 = new Set(sorted.filter(x => x.v > 0).slice(-3).map(x => x.i));
    return { monthly, max, mean, top3, bottom3 };
  }, [presenze]);

  // Heatmap: all territories, monthly presenze
  const territoryHeatmap = useMemo(() => {
    if (allIndicators.length === 0) return {};
    const grouped: Record<string, { mese_picco: number; sr: number | null; p_totale: number }> = {};
    allIndicators.forEach((ind: SeasonalityIndicator) => {
      grouped[ind.territorio] = { mese_picco: ind.mese_picco, sr: ind.sr, p_totale: ind.p_totale };
    });
    return grouped;
  }, [allIndicators]);

  if (authLoading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
        >
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <TrendingDown className="h-6 w-6 text-[#284899]" />
              Destagionalizzazione
            </h1>
            <p className="text-muted-foreground mt-1">
              Analizza i periodi a bassa domanda e pianifica strategie promozionali
            </p>
          </div>

          {/* Avvisi and Preferenze buttons removed — no longer tracked */}
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex border-b border-border">
          {[
            { id: "stagionalita" as const, label: "Stagionalita" },
            { id: "arrivi" as const, label: "Arrivi e Presenze" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px",
                activeTab === tab.id
                  ? "border-[#284899] text-[#284899]"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab: Arrivi e Presenze */}
        {activeTab === "arrivi" && (
          <ArriviPresenzeSection
            anno={effectiveAnno}
            yearOptions={yearOptions}
            onAnnoChange={(val) => setIstatAnno(val)}
            initialProvincia={istatTerritorio}
          />
        )}

        {/* Tab: Stagionalita (unified) */}
        {activeTab !== "arrivi" && (
          <>
            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
              <Autocomplete
                options={territoryOptions}
                value={istatTerritorio || "Italia"}
                onChange={(val) => setIstatTerritorio(val || "Italia")}
                placeholder="Seleziona territorio..."
                className="min-w-[220px]"
                clearable={istatTerritorio !== "Italia"}
              />

              {yearOptions.length > 0 && (
                <Autocomplete
                  options={yearOptions}
                  value={effectiveAnno ? String(effectiveAnno) : ""}
                  onChange={(val) => setIstatAnno(Number(val) || effectiveAnno)}
                  placeholder="Anno"
                  className="w-[100px]"
                  clearable={false}
                />
              )}

              <Autocomplete
                options={RESIDENZE.map(r => ({ value: r.value, label: r.label }))}
                value={istatResidenza}
                onChange={(val) => setIstatResidenza(val || "mondo")}
                placeholder="Provenienza"
                className="min-w-[170px]"
                clearable={false}
              />

              <Autocomplete
                options={TIPOLOGIE.map(t => ({ value: t.value, label: t.label }))}
                value={istatTipologia}
                onChange={(val) => setIstatTipologia(val || "totale")}
                placeholder="Tipo struttura"
                className="min-w-[190px]"
                clearable={false}
              />

              <div className="flex rounded-lg border border-border overflow-hidden">
                {[
                  { id: "bar" as const, icon: BarChart3, label: "Barre" },
                  { id: "line" as const, icon: LineChart, label: "Linea" },
                ].map((view) => (
                  <button
                    key={view.id}
                    onClick={() => setChartView(view.id)}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-2 text-sm transition-colors",
                      chartView === view.id
                        ? "bg-[#284899] text-white"
                        : "bg-card text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                    )}
                  >
                    <view.icon className="h-4 w-4" />
                    {view.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Stats Cards — from ISTAT data */}
            {currentIndicator && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  {
                    icon: Sun,
                    bg: "bg-amber-500/20", iconColor: "text-amber-500",
                    value: monthNames[(currentIndicator.mese_picco || 1) - 1],
                    sub: formatNum(currentIndicator.p_max) + " presenze",
                    label: "Mese di picco",
                  },
                  {
                    icon: TrendingUp,
                    ...(currentIndicator.sr !== null && currentIndicator.sr > 4
                      ? { bg: "bg-red-500/20", iconColor: "text-red-500" }
                      : currentIndicator.sr !== null && currentIndicator.sr >= 2
                        ? { bg: "bg-gray-400/20", iconColor: "text-gray-400" }
                        : { bg: "bg-emerald-500/20", iconColor: "text-emerald-500" }),
                    value: currentIndicator.sr !== null ? currentIndicator.sr.toFixed(1) : "-",
                    sub: "Pmax / Pmin",
                    label: "Tasso di stagionalità (SR)",
                  },
                  {
                    icon: BarChart3,
                    ...(currentIndicator.pf !== null && currentIndicator.pf > 4
                      ? { bg: "bg-red-500/20", iconColor: "text-red-500" }
                      : currentIndicator.pf !== null && currentIndicator.pf >= 2
                        ? { bg: "bg-gray-400/20", iconColor: "text-gray-400" }
                        : { bg: "bg-emerald-500/20", iconColor: "text-emerald-500" }),
                    value: currentIndicator.pf !== null ? currentIndicator.pf.toFixed(2) : "-",
                    sub: "Pmax / Pmedio",
                    label: "Fattore di picco stagionale (PF)",
                  },
                  {
                    icon: Target,
                    ...(currentIndicator.c3_pct !== null && currentIndicator.c3_pct > 50
                      ? { bg: "bg-red-500/20", iconColor: "text-red-500" }
                      : { bg: "bg-gray-400/20", iconColor: "text-gray-400" }),
                    value: currentIndicator.c3_pct !== null ? currentIndicator.c3_pct + "%" : "-",
                    sub: "Top 3 mesi / Totale",
                    label: "Concentrazione domanda (C3%)",
                  },
                ].map((card, i) => (
                  <motion.div key={card.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", card.bg)}>
                            <card.icon className={cn("h-5 w-5", card.iconColor)} />
                          </div>
                          <div>
                            <p className="text-2xl font-bold">{card.value}</p>
                            <p className="text-xs text-muted-foreground">{card.label}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Chart Area */}
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      {chartView === "bar" ? (
                        <BarChart3 className="h-4 w-4 text-[#284899]" />
                      ) : (
                        <LineChart className="h-4 w-4 text-[#284899]" />
                      )}
                      Presenze mensili {effectiveAnno}
                      {istatTerritorio && <Badge variant="secondary">{istatTerritorio}</Badge>}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!istatTerritorio ? (
                      <TerritoryHeatmap
                        heatmap={territoryHeatmap}
                        onSelect={setIstatTerritorio}
                      />
                    ) : presenzeLoading ? (
                      <div className="flex items-center justify-center h-64">
                        <Spinner size="lg" />
                      </div>
                    ) : chartData ? (
                      <IstatChart chartData={chartData} chartType={chartView} />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-64">
                        <BarChart3 className="h-12 w-12 text-muted-foreground opacity-30 mb-4" />
                        <h3 className="text-lg font-semibold mb-2">Nessun dato disponibile</h3>
                        <p className="text-muted-foreground text-sm">
                          Seleziona un territorio dal filtro
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Comparison toggle */}
                {istatTerritorio && (
                  <div className="mt-4">
                    <Button
                      variant="outline"
                      onClick={() => setShowComparison(!showComparison)}
                      className="mb-3 w-full py-3 text-base"
                    >
                      <ArrowLeftRight className="h-5 w-5 mr-2" />
                      {showComparison ? "Nascondi confronto" : "Confronta con altro territorio"}
                    </Button>

                    {showComparison && (
                      <div className="space-y-3">
                        <Autocomplete
                          options={comparisonOptions}
                          value={comparisonTerritory || ""}
                          onChange={(val) => setComparisonTerritory(val || null)}
                          placeholder="Seleziona territorio..."
                          className="min-w-[220px]"
                        />
                        {comparisonTerritory && (
                          <ComparisonChart
                            seriesA={presenze}
                            seriesB={comparisonPresenze}
                            labelA={istatTerritorio}
                            labelB={comparisonTerritory}
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Suggestions */}
                {currentIndicator && currentIndicator.sr !== null && currentIndicator.sr > 5 && (
                  <Card className="mt-6 border-[#284899]/30 bg-[#284899]/5">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Lightbulb className="h-4 w-4 text-[#284899]" />
                        Suggerimenti per {istatTerritorio}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          Stagionalita elevata (SR = {currentIndicator.sr}). Il {currentIndicator.c3_pct}% delle presenze si concentra in soli 3 mesi.
                          Mese piu debole: <span className="font-medium text-foreground">{monthNames[(currentIndicator.mese_minimo || 1) - 1]}</span>.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {[
                            { icon: Target, title: "Crea Pianificazioni Speciali", desc: "Offri pianificazioni a prezzo ridotto per i periodi di bassa stagione" },
                            { icon: Info, title: "Promuovi Eventi Locali", desc: "Organizza eventi tematici per attirare visitatori" },
                          ].map((s, i) => (
                            <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-background border border-border">
                              <s.icon className="h-4 w-4 text-[#284899] mt-0.5 flex-shrink-0" />
                              <div>
                                <h5 className="font-medium text-sm">{s.title}</h5>
                                <p className="text-xs text-muted-foreground">{s.desc}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Indicators panel */}
                {currentIndicator && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Indicatori</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Presenze totali annue</span>
                        <span className="font-medium">{formatNum(currentIndicator.p_totale)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Media mensile presenze</span>
                        <span className="font-medium">{formatNum(currentIndicator.p_medio)}</span>
                      </div>
                      <hr className="my-2" />
                      {([
                        { key: "sr", label: "Tasso di stagionalità", val: currentIndicator.sr, thresholds: [2, 4] as const },
                        { key: "si", label: "Intensità stagionale", val: currentIndicator.si, thresholds: null },
                        { key: "pf", label: "Fattore di picco stagionale", val: currentIndicator.pf, thresholds: [2, 4] as const },
                      ] as const).map(row => {
                        const dotColor = row.val !== null && row.thresholds
                          ? (row.val as number) > row.thresholds[1] ? "#d62728" : (row.val as number) >= row.thresholds[0] ? "#9ca3af" : "#2ca02c"
                          : undefined;
                        return (
                          <div key={row.key} className="flex justify-between items-center">
                            <span className="text-muted-foreground">{row.label}</span>
                            <span className="font-medium flex items-center gap-1">
                              {formatNum(row.val)}
                              {dotColor && (
                                <span
                                  className="inline-block w-3 h-3 rounded-full"
                                  style={{ backgroundColor: dotColor }}
                                />
                              )}
                            </span>
                          </div>
                        );
                      })}
                      <hr className="my-2" />
                      {/* Concentrazione alta (top 3 mesi) */}
                      <div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">Concentrazione alta<br /><span className="text-xs">(3 mesi di picco)</span></span>
                          <span className="font-medium text-red-500">
                            {currentIndicator.c3_pct !== null ? currentIndicator.c3_pct + "%" : "-"}
                          </span>
                        </div>
                        {chartData && (
                          <p className="text-xs text-red-500 mt-0.5">
                            {Array.from(chartData.top3).sort((a, b) => a - b).map(i => monthNames[i]).join(" – ")}
                          </p>
                        )}
                      </div>
                      {/* Concentrazione bassa (bottom 3 mesi) */}
                      {chartData && currentIndicator.p_totale > 0 && (
                        <div>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Concentrazione bassa<br /><span className="text-xs">(3 mesi di minimo)</span></span>
                            <span className="font-medium text-emerald-500">
                              {(() => {
                                const bottom3Sum = Array.from(chartData.bottom3).reduce((s, i) => s + chartData.monthly[i], 0);
                                return ((bottom3Sum / currentIndicator.p_totale) * 100).toFixed(1) + "%";
                              })()}
                            </span>
                          </div>
                          <p className="text-xs text-emerald-500 mt-0.5">
                            {Array.from(chartData.bottom3).sort((a, b) => a - b).map(i => monthNames[i]).join(" – ")}
                          </p>
                        </div>
                      )}
                      {currentCapacity && (
                        <>
                          <hr className="my-2" />
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Capacità ricettiva (posti letto)</span>
                            <span className="font-medium">{formatNum(currentCapacity.pl_prov)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Capacità massima annua (posti letto-giorno)</span>
                            <span className="font-medium">{formatNum(currentCapacity.cpla_prov)}</span>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Monthly Breakdown */}
                {istatTerritorio && chartData && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Dettaglio Mensile</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {chartData.monthly.map((val, i) => {
                          const season = getSeasonFromMonth(i + 1);
                          const SeasonIcon = seasonIcons[season];
                          const pct = chartData.max > 0 ? (val / chartData.max) * 100 : 0;
                          const isTop = chartData.top3.has(i);
                          const isBottom = chartData.bottom3.has(i);
                          return (
                            <div
                              key={i}
                              className={cn(
                                "flex items-center gap-3 p-2 rounded-lg transition-colors",
                                isBottom && "bg-emerald-500/10 border border-emerald-500/20",
                                isTop && "bg-red-500/10 border border-red-500/20"
                              )}
                            >
                              <SeasonIcon className={cn(
                                "h-4 w-4",
                                season === "summer" && "text-[#284899]",
                                season === "winter" && "text-blue-400",
                                season === "spring" && "text-emerald-500",
                                season === "autumn" && "text-[#284899]"
                              )} />
                              <span className="text-sm font-medium w-12">{monthNames[i]}</span>
                              <div className="flex-1">
                                <div className="h-2 rounded-full bg-secondary overflow-hidden">
                                  <div
                                    className={cn(
                                      "h-full rounded-full transition-all",
                                      isTop && "bg-red-500",
                                      isBottom && "bg-emerald-500",
                                      !isTop && !isBottom && "bg-[#284899]"
                                    )}
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              </div>
                              <span className="text-xs font-medium w-14 text-right">
                                {formatNum(val)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Legend */}
                <Card>
                  <CardContent className="p-4">
                    <h4 className="text-sm font-medium mb-3">Legenda</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500" />
                        <span className="text-muted-foreground">Top 3 mesi (alta stagione)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[#284899]" />
                        <span className="text-muted-foreground">Stagione intermedia</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-emerald-500" />
                        <span className="text-muted-foreground">Bottom 3 mesi (bassa stagione)</span>
                      </div>
                      <hr className="my-2" />
                      <p className="text-xs text-muted-foreground">Classificazione indicatori:</p>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-[#2ca02c]" />
                        <span className="text-muted-foreground">Basso</span>
                        <div className="w-3 h-3 rounded-full bg-[#9ca3af] ml-2" />
                        <span className="text-muted-foreground">Medio</span>
                        <div className="w-3 h-3 rounded-full bg-[#d62728] ml-2" />
                        <span className="text-muted-foreground">Alto</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Preferences Modal removed — no longer tracked */}
          </>
        )}
      </div>
    </div>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

function IstatChart({
  chartData,
  chartType,
}: {
  chartData: { monthly: number[]; max: number; mean: number; top3: Set<number>; bottom3: Set<number> };
  chartType: "bar" | "line";
}) {
  const { monthly, max, mean, top3, bottom3 } = chartData;
  const svgW = 600;
  const svgH = 280;
  const padL = 60;
  const padR = 20;
  const padT = 28;
  const padB = 40;
  const plotW = svgW - padL - padR;
  const plotH = svgH - padT - padB;
  const barW = plotW / 12 * 0.6;

  const scaleY = (v: number) => padT + plotH - (max > 0 ? (v / max) * plotH : 0);
  const scaleX = (i: number) => padL + (i + 0.5) * (plotW / 12);

  const linePoints = monthly.map((v, i) => `${scaleX(i)},${scaleY(v)}`).join(" ");

  return (
    <div>
      <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map(frac => {
          const y = padT + plotH * (1 - frac);
          return (
            <g key={frac}>
              <line x1={padL} y1={y} x2={svgW - padR} y2={y} stroke="#e5e7eb" strokeWidth={0.5} />
              <text x={padL - 6} y={y + 4} textAnchor="end" fontSize={10} fill="#9ca3af">
                {formatNum(max * frac)}
              </text>
            </g>
          );
        })}

        {/* Bars */}
        {monthly.map((v, i) => {
          const isTop = top3.has(i);
          const isBottom = bottom3.has(i);
          const barH = max > 0 ? (v / max) * plotH : 0;
          const baseline = padT + plotH;

          if (chartType === "bar") {
            return (
              <motion.rect
                key={i}
                x={scaleX(i) - barW / 2}
                width={barW}
                fill={isTop ? "#ef4444" : isBottom ? "#10b981" : "#284899"}
                opacity={0.8}
                rx={2}
                initial={{ height: 0 }}
                animate={{ height: barH }}
                y={baseline - barH}
                transition={{ delay: i * 0.04, duration: 0.3 }}
              />
            );
          }
          // Background highlight bars for line chart
          const fill = isTop ? "rgba(239,68,68,0.12)" : isBottom ? "rgba(16,185,129,0.12)" : "rgba(156,163,175,0.06)";
          return (
            <rect
              key={i}
              x={scaleX(i) - barW / 2}
              y={baseline - barH}
              width={barW}
              height={barH}
              fill={fill}
              rx={2}
            />
          );
        })}

        {/* Value labels on top/bottom bars */}
        {monthly.map((v, i) => {
          const isTop = top3.has(i);
          const isBottom = bottom3.has(i);
          if (!isTop && !isBottom) return null;
          return (
            <text
              key={`lbl-${i}`}
              x={scaleX(i)}
              y={scaleY(v) - 6}
              textAnchor="middle"
              fontSize={10}
              fontWeight={isTop ? 700 : 400}
              fill={isTop ? "#ef4444" : "#10b981"}
            >
              {formatNum(v)}
            </text>
          );
        })}

        {/* Mean line */}
        <line
          x1={padL} y1={scaleY(mean)}
          x2={svgW - padR} y2={scaleY(mean)}
          stroke="#9ca3af" strokeWidth={1} strokeDasharray="6,4"
        />

        {/* Line chart overlay */}
        {chartType === "line" && (
          <>
            <defs>
              <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#284899" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#284899" stopOpacity={0} />
              </linearGradient>
            </defs>
            <polygon
              points={`${scaleX(0)},${padT + plotH} ${linePoints} ${scaleX(11)},${padT + plotH}`}
              fill="url(#lineGrad)"
            />
            <polyline
              points={linePoints}
              fill="none"
              stroke="#284899"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {monthly.map((v, i) => (
              <circle
                key={i}
                cx={scaleX(i)}
                cy={scaleY(v)}
                r={3.5}
                fill={top3.has(i) ? "#ef4444" : bottom3.has(i) ? "#10b981" : "#284899"}
                stroke="white"
                strokeWidth={1.5}
              />
            ))}
          </>
        )}

        {/* X axis */}
        {monthNames.map((label, i) => (
          <text key={i} x={scaleX(i)} y={svgH - 8} textAnchor="middle" fontSize={11} fill="#6b7280">
            {label}
          </text>
        ))}
      </svg>

      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground justify-center">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-red-500/80" /> Top 3
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-emerald-500/80" /> Bottom 3
        </span>
        <span className="flex items-center gap-1">
          <span className="w-8 border-t border-dashed border-gray-400" /> Media
        </span>
      </div>
    </div>
  );
}

function TerritoryHeatmap({
  heatmap,
  onSelect,
}: {
  heatmap: Record<string, { mese_picco: number; sr: number | null; p_totale: number }>;
  onSelect: (territory: string) => void;
}) {
  const [view, setView] = useState<"heatmap" | "tabella">("heatmap");

  const sorted = useMemo(() => {
    return Object.entries(heatmap).sort(([, a], [, b]) => b.p_totale - a.p_totale);
  }, [heatmap]);


  if (sorted.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Spinner size="lg" />
        <p className="text-sm text-muted-foreground mt-4">Caricamento territori...</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* View toggle */}
      <div className="flex items-center gap-1 bg-secondary/50 rounded-lg p-0.5 w-fit">
        <button
          onClick={() => setView("heatmap")}
          className={cn(
            "px-3 py-1 rounded-md text-xs font-medium transition-colors",
            view === "heatmap" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
          )}
        >
          Heatmap
        </button>
        <button
          onClick={() => setView("tabella")}
          className={cn(
            "px-3 py-1 rounded-md text-xs font-medium transition-colors",
            view === "tabella" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
          )}
        >
          Tabella
        </button>
      </div>

      {view === "heatmap" ? (
        <div className="max-h-[400px] overflow-y-auto overflow-x-auto">
          {/* Month header */}
          <div className="flex items-center gap-0 sticky top-0 bg-card z-10 pb-1 border-b border-border">
            <span className="w-28 min-w-[7rem] text-xs font-medium text-muted-foreground shrink-0">Territorio</span>
            {monthNames.map((m) => (
              <span key={m} className="w-8 min-w-[2rem] text-center text-[10px] text-muted-foreground">{m}</span>
            ))}
          </div>
          {/* Rows */}
          {sorted.slice(0, 50).map(([territory, data]) => (
            <button
              key={territory}
              onClick={() => onSelect(territory)}
              className="flex items-center gap-0 w-full text-left py-0.5 rounded hover:bg-secondary/30 transition-colors group"
            >
              <span className="w-28 min-w-[7rem] text-xs font-medium truncate shrink-0 group-hover:text-[#284899]">{territory}</span>
              {Array.from({ length: 12 }, (_, i) => {
                const month = i + 1;
                const isPeak = data.mese_picco === month;
                const distance = Math.abs(month - data.mese_picco);
                const nearPeak = distance <= 1 || distance >= 11;
                const sr = data.sr ?? 0;

                let bg = "bg-transparent";
                if (isPeak) {
                  bg = sr > 4 ? "bg-red-500" : sr >= 2 ? "bg-amber-400" : "bg-emerald-400";
                } else if (nearPeak) {
                  bg = sr > 4 ? "bg-red-300/60" : sr >= 2 ? "bg-amber-300/50" : "bg-emerald-300/40";
                }

                return (
                  <span
                    key={month}
                    className={cn("w-8 min-w-[2rem] h-5 rounded-sm mx-px", bg)}
                    title={`${territory} - ${monthNames[i]}${isPeak ? " (picco)" : ""}`}
                  />
                );
              })}
            </button>
          ))}
          {/* Legend */}
          <div className="flex items-center gap-4 pt-3 mt-2 border-t border-border text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-400" /> Bassa stagionalita</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-400" /> Media</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-500" /> Alta stagionalita</span>
          </div>
        </div>
      ) : (
        <div className="space-y-1 max-h-[400px] overflow-y-auto">
          <div className="flex items-center gap-2 text-xs text-muted-foreground pb-2 border-b sticky top-0 bg-card">
            <span className="w-32 font-medium">Territorio</span>
            <span className="w-16 text-right">Presenze</span>
            <span className="w-12 text-right">SR</span>
            <span className="w-12 text-right">Picco</span>
          </div>
          {sorted.slice(0, 50).map(([territory, data]) => (
            <button
              key={territory}
              onClick={() => onSelect(territory)}
              className="flex items-center gap-2 w-full text-left px-1 py-1.5 rounded hover:bg-secondary/50 transition-colors text-sm"
            >
              <span className="w-32 truncate font-medium">{territory}</span>
              <span className="w-16 text-right text-muted-foreground">{formatNum(data.p_totale)}</span>
              <span className={cn(
                "w-12 text-right font-medium",
                data.sr !== null && data.sr > 4 ? "text-red-500" : data.sr !== null && data.sr >= 2 ? "text-muted-foreground" : "text-emerald-500"
              )}>
                {data.sr !== null ? data.sr.toFixed(1) : "-"}
              </span>
              <span className="w-12 text-right text-muted-foreground">{monthNames[(data.mese_picco || 1) - 1]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
